import { assertNever } from 'owa-assert';
import type { MailboxInfo } from 'owa-client-types';
import { isAccountFeatureEnabled, isGlobalFeatureEnabled } from 'owa-feature-flags';
import { OcpsPolicyFeature } from '../store/schema/OutlookCloudPolicyTypes';

type FlightCheck = (mailboxInfo: MailboxInfo) => boolean;

interface OcpsPolicyFeatureId {
    // CPS featureId (`<catalog>;L_<Feature>`) as the service knows it.
    featureId: string;
    isEnabled: FlightCheck;
}

// Features onboarding a new policy also require the restart-after-policy-change flight, since a
// policy change must be able to prompt a restart. Features that only extend an already shipped
// policy surface check just their own rollout flight.
const isPrivacyFlightEnabled: FlightCheck = () =>
    isGlobalFeatureEnabled('policy-ocps-owsprime-privacy');

const isUnifiedInboxFlightEnabled: FlightCheck = mailboxInfo =>
    isGlobalFeatureEnabled('acct-unifiedInbox') &&
    isAccountFeatureEnabled('policy-restartAfterPoliciesChange', mailboxInfo);

const isKeyboardStylesFlightEnabled: FlightCheck = mailboxInfo =>
    isAccountFeatureEnabled('policy-restartAfterPoliciesChange', mailboxInfo);

const isPresenceFlightEnabled: FlightCheck = () =>
    isGlobalFeatureEnabled('mon-presence-policy-checks');

// A client feature can map to several CPS featureIds (privacy alone owns four), so featureIds are
// listed flat with the flight that gates them rather than derived from the settingId map.
const ocpsPolicyFeatureIds: OcpsPolicyFeatureId[] = [
    { featureId: 'office16;L_OptionalConnectedExperiences', isEnabled: isPrivacyFlightEnabled },
    { featureId: 'office16;L_ConnectedOfficeExperiences', isEnabled: isPrivacyFlightEnabled },
    // Beware of the typo "Anlayzing" - this is how the server stores it
    {
        featureId: 'office16;L_OfficeExperiencesAnlayzingContent',
        isEnabled: isPrivacyFlightEnabled,
    },
    {
        featureId: 'office16;L_OfficeExperiencesDownloadingContent',
        isEnabled: isPrivacyFlightEnabled,
    },
    { featureId: 'outlooknew;L_ShowAllAccountsView', isEnabled: isUnifiedInboxFlightEnabled },
    { featureId: 'outlooknew;L_KeyboardStyles', isEnabled: isKeyboardStylesFlightEnabled },
    { featureId: 'office16;L_TurnOffPresenceIntegration', isEnabled: isPresenceFlightEnabled },
    { featureId: 'office16;L_ConfigurePresenceIcons', isEnabled: isPresenceFlightEnabled },
];

/**
 * Returns true when every flight gating an OCPS policy feature is on. The store uses this to decide
 * whether policy data is applicable, so consumers never check the rollout flight themselves.
 */
export function isOcpsPolicyFeatureEnabled(
    feature: OcpsPolicyFeature,
    mailboxInfo: MailboxInfo
): boolean {
    switch (feature) {
        case OcpsPolicyFeature.Privacy:
            return isPrivacyFlightEnabled(mailboxInfo);
        case OcpsPolicyFeature.UnifiedInbox:
            return isUnifiedInboxFlightEnabled(mailboxInfo);
        case OcpsPolicyFeature.KeyboardStyles:
            return isKeyboardStylesFlightEnabled(mailboxInfo);
        case OcpsPolicyFeature.Presence:
            return isPresenceFlightEnabled(mailboxInfo);
        default:
            return assertNever(feature);
    }
}

/**
 * The CPS featureIds the app consumes with their flights turned on for the account. OWS Prime uses
 * this list to scope policy change detection to what the app actually reads.
 */
export function getEnabledOcpsPolicyFeatureIds(mailboxInfo: MailboxInfo): string[] {
    return ocpsPolicyFeatureIds
        .filter(({ isEnabled }) => isEnabled(mailboxInfo))
        .map(({ featureId }) => featureId);
}
